import { useCallback, useMemo } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { LabelType, LabelValueSource } from 'graphql/types';

import { FormTimesheetLabel } from '../labelSchema';

type useBulkLabelSuggestionsActionsResult = {
  acceptAllLabelSuggestionHandler: () => void;
  denyAllLabelSuggestionHandler: () => void;
  enableTimesheetActions: boolean;
};
type useBulkLabelSuggestionsActionsProps = {
  fieldName: string;
};
export const useBulkLabelSuggestionsActions = ({
  fieldName
}: useBulkLabelSuggestionsActionsProps): useBulkLabelSuggestionsActionsResult => {
  const { setValue, control } = useFormContext();

  const fields = useWatch({
    name: `${fieldName}`,
    control
  });

  const enableTimesheetActions = useMemo(
    () =>
      (fields ?? []).some(
        (field: FormTimesheetLabel) =>
          field?.label?.valueSource === LabelValueSource.Suggestion &&
          field?.label?.type !== LabelType.Comment &&
          field?.label?.value &&
          !isEmpty(field?.label?.value)
      ),
    [fields]
  );
  const setLabelValue = useCallback(
    (name: string, value: FieldValues) => {
      setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [setValue]
  );
  const acceptAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      if (
        field?.label?.valueSource === LabelValueSource.Suggestion &&
        field?.label?.value
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          valueSource: LabelValueSource.User
        });
      }
    });
  }, [fields, setLabelValue, fieldName]);

  const denyAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      if (
        field?.label?.valueSource === LabelValueSource.Suggestion &&
        field?.label?.value
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          value: null,
          valueSource: LabelValueSource.Suggestion
        });
      }
    });
  }, [fields, setLabelValue, fieldName]);

  return {
    enableTimesheetActions,
    acceptAllLabelSuggestionHandler,
    denyAllLabelSuggestionHandler
  };
};
